/* eslint-disable react-hooks/refs */
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  createSocketConnection,
  registerSocketHandlers,
  unregisterSocketHandlers,
} from "@api/socketClient";
import { useChatStore } from "@stores/chatStore";
import { store } from "@stores/redux/store";
import { addMessage as addMessageToRedux } from "@features/messages/messagesSlice";
import type { Message, Conversation } from "@interfaces/chat.interface";
import type { ConversationStateChangedPayload } from "@interfaces/api.interface";
import { ConversationTabHelpers, TAB_CONFIG } from "@constants/conversationTabs";

/**
 * Normalize direction: "inbound"/"outbound" → "INBOUND"/"OUTBOUND"
 */
const normalizeDirection = (
  direction?: string
): "INBOUND" | "OUTBOUND" | undefined => {
  if (!direction) return undefined;
  const upper = direction.toUpperCase();
  return upper === "INBOUND" || upper === "OUTBOUND" ? upper : undefined;
};

/**
 * Transform extraData for Zalo messages
 */
const transformZaloExtraData = (
  messageType: string,
  extraData: unknown,
  provider?: string
): Partial<Message["meta"]> => {
  if (provider !== "zalo" || !extraData || typeof extraData !== "object") {
    return {};
  }

  const extra = extraData as Record<string, unknown>;
  const meta: Partial<Message["meta"]> = {};

  switch (messageType) {
    case "TEMPLATE": {
      // Get templateUrl directly from extraData.url
      if (extra.url && typeof extra.url === "string") {
        meta.templateUrl = extra.url;
        meta.originalMessageType = "TEMPLATE";
      }
      break;
    }

    case "LIST": {
      // Get data directly from extraData.*
      const title = (extra.title as string) || "";
      const thumbnail = (extra.thumbnail as string) || "";
      const description = (extra.description as string) || "";
      const url = (extra.url as string) || "";
      const type = (extra.type as string) || "link";

      meta.attachmentJson = JSON.stringify({
        text: title,
        attachments: [
          {
            type: type,
            payload: {
              thumbnail: thumbnail,
              description: description,
              title: title,
              url: url,
            },
          },
        ],
      });
      meta.originalMessageType = "LIST";
      break;
    }

    case "CALL_REQUEST": {
      const callDuration = extra.call_duration;
      if (callDuration !== undefined) {
        meta.callDuration =
          typeof callDuration === "string"
            ? parseInt(callDuration, 10)
            : typeof callDuration === "number"
              ? callDuration
              : 0;
      }
      meta.originalMessageType = "CALL_REQUEST";
      break;
    }

    case "STICKER":
      if (extra.url && typeof extra.url === "string") {
        meta.previewUrl = extra.url;
      }
      meta.originalMessageType = "STICKER";
      break;

    case "IMAGE":
    case "VIDEO":
    case "AUDIO":
      if (extra.url || extra.thumbnail) {
        meta.attachments = [
          {
            type: messageType.toLowerCase(),
            payload: {
              url: extra.url || "",
              thumbnail: extra.thumbnail || "",
            },
            url: extra.url || "",
          },
        ];
      }
      break;
  }

  return meta;
};

const normalizeIncomingMessage = (
  rawMessage: Message & {
    timestampMs?: number;
    timestamp?: number;
    messageType?: string;
    direction?: string;
    content?: string;
    senderId?: string;
    senderName?: string;
    senderAvatar?: string;
    provider?: string;
    actorSource?: string;
    extraData?: unknown;
  }
) => {
  if (!rawMessage || !rawMessage.conversationId) return null;

  const timestamp =
    rawMessage.timestampMs ??
    rawMessage.timestamp ??
    rawMessage.createdAt ??
    Date.now();
  const createdAt =
    typeof rawMessage.createdAt === "string"
      ? rawMessage.createdAt
      : new Date(timestamp).toISOString();
  const typeMap: Record<string, Message["type"]> = {
    TEXT: "text",
    IMAGE: "image",
    VIDEO: "video",
    FILE: "file",
    LINK: "link",
    AUDIO: "audio",
    TEMPLATE: "text",
    LIST: "link",
    CALL_REQUEST: "text",
    STICKER: "image",
  };

  const type =
    rawMessage.type || typeMap[rawMessage.messageType ?? ""] || "text";

  // Normalize direction (lowercase → uppercase)
  const rawDirection =
    rawMessage.direction || rawMessage.meta?.direction || "";
  const normalizedDirection = normalizeDirection(rawDirection);

  const status: Message["status"] =
    rawMessage.status ||
    (normalizedDirection === "OUTBOUND" ? "sent" : "read");

  // Transform extraData for Zalo messages
  const messageProvider =
    typeof rawMessage.provider === "string"
      ? rawMessage.provider
      : typeof rawMessage.meta?.provider === "string"
        ? rawMessage.meta.provider
        : undefined;
  const extraDataMeta = transformZaloExtraData(
    rawMessage.messageType || "",
    rawMessage.extraData,
    messageProvider
  );

  // For STICKER, use URL from extraData.url or content as body
  let body =
    rawMessage.body ??
    rawMessage.content ??
    (rawMessage as { text?: string }).text ??
    "";
  if (rawMessage.messageType === "STICKER") {
    if (
      rawMessage.extraData &&
      typeof rawMessage.extraData === "object"
    ) {
      const extra = rawMessage.extraData as Record<string, unknown>;
      if (extra.url && typeof extra.url === "string") {
        body = extra.url;
      } else if (rawMessage.content && typeof rawMessage.content === "string" && rawMessage.content.startsWith("http")) {
        body = rawMessage.content;
      }
    } else if (rawMessage.content && typeof rawMessage.content === "string" && rawMessage.content.startsWith("http")) {
      body = rawMessage.content;
    }
  }

  const sender = {
    id: rawMessage.sender?.id ?? rawMessage.senderId ?? "",
    name: rawMessage.sender?.name ?? rawMessage.senderName ?? "",
    avatar: rawMessage.sender?.avatar ?? rawMessage.senderAvatar ?? "",
  };

  // Ensure direction, provider, and actorSource are in meta field
  const metaProvider = rawMessage.provider || rawMessage.meta?.provider;
  const actorSource = rawMessage.actorSource || rawMessage.meta?.actorSource;

  // Build meta object safely
  const meta: Message["meta"] = rawMessage.meta ? { ...rawMessage.meta } : {};
  if (normalizedDirection) {
    meta.direction = normalizedDirection;
  }
  if (metaProvider) {
    meta.provider = metaProvider;
  }
  if (actorSource) {
    meta.actorSource = actorSource;
  }

  // Merge extraData meta into meta object
  Object.assign(meta, extraDataMeta);

  return {
    ...rawMessage,
    sender,
    body,
    createdAt,
    type,
    status,
    meta: Object.keys(meta).length > 0 ? meta : rawMessage.meta,
  } as Message;
};

const transformStateChangedPayloadToConversation = (
  payload: ConversationStateChangedPayload
): Conversation | null => {
  const { conversationId, extraData, timestamp, provider } = payload;
  
  if (!extraData || !conversationId) {
    return null;
  }

  const channelMap: Record<string, "facebook" | "zalo" | "shopee" | "tiktok"> = {
    facebook: "facebook",
    zalo: "zalo",
    shopee: "shopee",
    tiktok: "tiktok",
  };
  
  const channel = channelMap[provider?.toLowerCase()] || "facebook";

  const messageType = (extraData.messageType?.toLowerCase() || "text") as Message["type"];
  const messageBody = messageType === "text" ? extraData.lastMessageSnippet : undefined;

  // Tạo lastMessage từ extraData
  const lastMessage: Message = {
    id: `state_changed_${conversationId}_${timestamp}`,
    conversationId,
    sender: {
      id: "system",
      name: extraData.customerName || "Unknown",
      avatar: extraData.avatar || "",
    },
    body: messageBody || "",
    createdAt: new Date(timestamp).toISOString(),
    status: "sent",
    type: messageType,
    meta: {
      direction: "INBOUND",
      messageType: "CONVERSATION_STATE_CHANGED",
    },
  };

  const conversation: Conversation = {
    id: conversationId,
    channel,
    name: extraData.customerName || "Unknown",
    avatar: extraData.avatar || "",
    isGroup: false,
    members: [],
    lastMessage,
    unreadCount: 0, // Có thể cần fetch từ API nếu cần
    isPinned: false,
    isMuted: false,
    typingUsers: [],
  };

  return conversation;
};

export const useSocket = (token?: string) => {
  const [isConnected, setIsConnected] = useState(false);
  const socket = useMemo(() => createSocketConnection(token), [token]);
  const upsertConversation = useChatStore((state) => state.upsertConversation);
  const addMessage = useChatStore((state) => state.addMessage);
  const updatePresence = useChatStore((state) => state.updatePresence);
  const updateConversationFolderStatus = useChatStore(
    (state) => state.updateConversationFolderStatus
  );

  const handlersRef = useRef({
    upsertConversation,
    addMessage,
    updatePresence,
    updateConversationFolderStatus,
  });
  handlersRef.current = { 
    upsertConversation, 
    addMessage, 
    updatePresence,
    updateConversationFolderStatus,
  };

  const handleIncomingMessage = useCallback((message: Message) => {
    const normalized = normalizeIncomingMessage(message);
    if (!normalized) return;

    // Skip template OUTBOUND messages from socket - they are already handled by optimistic updates
    const messageType =
      (message as { messageType?: string }).messageType ||
      (normalized.meta?.messageType as string | undefined);
    const direction =
      normalized.meta?.direction || (message as { direction?: string }).direction;

    if (messageType === "TEMPLATE" && direction === "OUTBOUND") {
      return;
    }

    const isActiveConversation =
      useChatStore.getState().selectedConversationId ===
      normalized.conversationId;

    handlersRef.current.addMessage(
      normalized.conversationId,
      normalized,
      isActiveConversation
    );

    store.dispatch(
      addMessageToRedux({
        conversationId: normalized.conversationId,
        message: normalized,
      })
    );
  }, []);

  const handleConversationStateChanged = useCallback(
    (payload: ConversationStateChangedPayload) => {
      const { conversationId, extraData } = payload;

      if (!extraData || !conversationId) {
        return;
      }

      // Only process if pageId matches the selectedPageId
      const currentState = useChatStore.getState();
      const selectedPageId = currentState.selectedPageId;

      if (extraData.pageId) {
        if (selectedPageId && extraData.pageId !== selectedPageId) {
          return;
        }
        if (!selectedPageId) {
          return;
        }
      } else {
        if (selectedPageId) {
          return;
        }
      }

      const activeTab = currentState.activeTab;
      const folder = extraData.folder;

      // Only upsert if folder matches current tab
      const shouldUpsertConversation = ConversationTabHelpers.isFolderMatchingTab(folder, activeTab);

      // Update folder status in store
      const folderStatus = extraData.folder === "ALL" ? "" : extraData.folder;
      handlersRef.current.updateConversationFolderStatus({
        [conversationId]: folderStatus,
      });

      // Check if conversation should be removed from current tab
      const previousFolder = currentState.conversationFolderStatusMap[conversationId];
      const expectedFolder = ConversationTabHelpers.getFolderForTab(activeTab);
      const isRemovingFromCurrentTab =
        expectedFolder !== undefined &&
        previousFolder === expectedFolder &&
        folderStatus !== expectedFolder;

      if (isRemovingFromCurrentTab) {
        // Deselect if currently selected
        if (currentState.selectedConversationId === conversationId) {
          useChatStore.getState().selectConversation(undefined);
          
          // Set moved conversation info to show banner (only for socket events)
          const setMovedConversationInfo = useChatStore.getState().setMovedConversationInfo;
          
          // Determine which tab the conversation moved to
          let targetTabLabel = "Tất cả";
          if (folderStatus === "ESCALATED") {
            targetTabLabel = TAB_CONFIG.ESCALATED.label;
          } else if (folderStatus === "DONE") {
            targetTabLabel = TAB_CONFIG.DONE.label;
          }
          
          setMovedConversationInfo({
            conversationId: conversationId,
            targetTabLabel: targetTabLabel,
          });
          
          // Auto-hide banner after 3 seconds
          setTimeout(() => {
            setMovedConversationInfo(null);
          }, 3000);
        }
        // Do not upsert if being removed from tab
        return;
      }

      if (!shouldUpsertConversation) {
        return;
      }

      // Transform payload to Conversation and upsert
      const conversation = transformStateChangedPayloadToConversation(payload);
      if (conversation) {
        // Try to get fanpageName from pageId in Redux
        let fanpageName: string | undefined;

        if (extraData.pageId) {
          const reduxState = store.getState();
          const facebookPages = reduxState.fanpage.pages;
          const matchedPage = facebookPages.find((page) => page.id === extraData.pageId);

          if (matchedPage) {
            fanpageName = matchedPage.name;
          }
        }

        // Fallback to selectedFanpageName if not found
        if (!fanpageName) {
          const currentState = useChatStore.getState();
          if (currentState.selectedFanpageName) {
            fanpageName = currentState.selectedFanpageName;
          }
        }
        if (fanpageName) {
          conversation.fanpageName = fanpageName;
        }
        handlersRef.current.upsertConversation(conversation);
      }
    },
    []
  );

  useEffect(() => {
    socket.connect();

    registerSocketHandlers(socket, {
      onConversationNew: handlersRef.current.upsertConversation,
      onConversationUpdate: handlersRef.current.upsertConversation,
      onMessageNew: handleIncomingMessage,
      onPresence: ({ userId, status }) =>
        handlersRef.current.updatePresence(userId, status),
      onConversationStateChanged: handleConversationStateChanged,
    });

    socket.on("connect", () => setIsConnected(true));
    socket.on("disconnect", () => setIsConnected(false));

    return () => {
      unregisterSocketHandlers(socket);
      socket.disconnect();
    };
  }, [handleIncomingMessage, handleConversationStateChanged, socket]);

  const emit = <T>(event: string, payload?: T) => {
    if (!socket.connected) return;
    socket.emit(event, payload);
  };

  return { socket, emit, isConnected };
};
